import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { ssoService, APP_CONFIGS } from '../services/sso';
import { toast } from 'react-toastify';

const AppMarketplace = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [recentApps, setRecentApps] = useState([]);
  const [launchingApp, setLaunchingApp] = useState(null);

  // Load recently used apps on mount
  useEffect(() => {
    setRecentApps(ssoService.getRecentApps());
  }, []);

  const quantrolixApps = [
    {
      id: 'fire-pos',
      name: 'Fire POS',
      icon: '\u{1F525}',
      category: 'restaurant',
      description: 'Complete restaurant point-of-sale system with table management, order tracking, and kitchen display.',
      price: 29,
      monthlyPrice: '$29/mo',
      features: ['Table Management', 'Kitchen Display', 'Split Bills', 'Reports'],
      status: 'available',
      users: '1,200+',
      rating: 4.8,
      url: 'https://apps.quantrolix.ai/fire-pos',
    },
    {
      id: 'menu-mentor',
      name: 'Menu Mentor',
      icon: '\u{1F4DA}',
      category: 'restaurant',
      description: 'Staff training platform with flashcards, quizzes, and spaced repetition for menu memorization.',
      price: 19,
      monthlyPrice: '$19/mo',
      features: ['Flashcards', 'Spaced Repetition', 'Progress Tracking', 'Certifications'],
      status: 'available',
      users: '450+',
      rating: 4.6,
      url: 'https://apps.quantrolix.ai/menu-mentor',
    },
    {
      id: 'claude-blob',
      name: 'Qode IDE',
      icon: '\u{1F4BB}',
      category: 'developer',
      description: 'AI-powered development environment with Monaco editor, voice control, integrated terminal, and multi-model Spark AI assistant.',
      price: 29,
      monthlyPrice: '$29/mo',
      features: ['Monaco Editor', 'Voice Control', 'Spark AI', 'Integrated Terminal'],
      status: 'beta',
      users: '200+',
      rating: 4.9,
      url: 'https://apps.quantrolix.ai/qode',
    },
    {
      id: 'tea-effects',
      name: 'Tea Effects',
      icon: '\u{1F375}',
      category: 'lifestyle',
      description: 'AI-powered tea recommendation engine based on mood, health goals, and time of day.',
      price: 5,
      monthlyPrice: '$5/mo',
      features: ['Mood Matching', 'Health Goals', 'Tea Database', 'Brew Timers'],
      status: 'available',
      users: '850+',
      rating: 4.9,
      url: 'https://apps.quantrolix.ai/tea-effects',
    },
    {
      id: 'yield-vault',
      name: 'YieldVault',
      icon: '\u{1F4C8}',
      category: 'finance',
      description: 'Inventory management and investment tracking with Stripe payments, yield calculations, and portfolio analytics.',
      price: 14.99,
      monthlyPrice: '$14.99/mo',
      features: ['Inventory Tracking', 'Stripe Payments', 'Yield Reports', 'Capacitor Mobile'],
      status: 'available',
      users: '320+',
      rating: 4.7,
      url: 'https://apps.quantrolix.ai/yield-vault',
    },
    {
      id: 'kasa-controller',
      name: 'Kasa Controller',
      icon: '\u{1F3E0}',
      category: 'smart-home',
      description: 'Smart home automation for TP-Link Kasa devices. Schedules, scenes, and energy monitoring with PostgreSQL backend.',
      price: 4.99,
      monthlyPrice: '$4.99/mo',
      features: ['Device Control', 'Schedules', 'Energy Monitoring', 'Scenes'],
      status: 'available',
      users: '780+',
      rating: 4.5,
      url: 'https://apps.quantrolix.ai/kasa-controller',
    },
    {
      id: 'architect-app',
      name: 'Architect 3D',
      icon: '\u{1F4D0}',
      category: 'design',
      description: '3D architectural visualization with React Three Fiber, GPU-accelerated rendering, and client collaboration tools.',
      price: 39,
      monthlyPrice: '$39/mo',
      features: ['3D Rendering', 'GPU Accelerated', 'Client Sharing', 'CAD Export'],
      status: 'available',
      users: '150+',
      rating: 4.4,
      url: 'https://apps.quantrolix.ai/architect-app',
    },
    {
      id: 'nexus-3d-maps',
      name: 'Nexus 3D Maps',
      icon: '\u{1F30D}',
      category: 'mapping',
      description: 'Interactive 3D mapping platform powered by Cesium.js with AR HUD overlays, geospatial analytics, and real-time data layers.',
      price: 24.99,
      monthlyPrice: '$24.99/mo',
      features: ['Cesium.js 3D Globe', 'AR HUD Overlay', 'Geospatial Data', 'Real-time Layers'],
      status: 'available',
      users: '180+',
      rating: 4.6,
      url: 'https://apps.quantrolix.ai/nexus-3d',
    },
    {
      id: 'grocery-optimizer',
      name: 'Grocery Optimizer',
      icon: '\u{1F6D2}',
      category: 'lifestyle',
      description: 'Optimize grocery shopping for protein goals, budget, and meal prep scheduling. Built for bodybuilders and athletes.',
      price: 0,
      monthlyPrice: 'Free',
      features: ['Protein Tracking', 'Budget Optimizer', 'Meal Prep', 'MCP Nutrition'],
      status: 'available',
      users: '500+',
      rating: 4.6,
      url: 'https://apps.quantrolix.ai/grocery-optimizer',
    },
    {
      id: 'motionforge',
      name: 'MotionForge',
      icon: '\u{1F3AC}',
      category: 'media',
      description: 'Professional video rendering studio powered by Remotion. Create programmatic videos, animations, and dynamic content at scale.',
      price: 19.99,
      monthlyPrice: '$19.99/mo',
      features: ['Remotion Engine', 'Template Library', 'Batch Rendering', 'Export Formats'],
      status: 'available',
      users: '250+',
      rating: 4.7,
      url: 'https://apps.quantrolix.ai/motionforge',
    },
    {
      id: 'phantomcast',
      name: 'PhantomCast',
      icon: '\u{1F4FA}',
      category: 'streaming',
      description: 'Streaming TV platform for Roku and Fire TV. Create and manage your own streaming channels with analytics.',
      price: 0,
      monthlyPrice: 'Free tier',
      features: ['Channel Creation', 'Analytics', 'Monetization', 'Multi-device'],
      status: 'available',
      users: '3,400+',
      rating: 4.7,
      url: 'https://apps.quantrolix.ai/phantomcast',
    },
    {
      id: 'api-marketplace',
      name: 'API Marketplace',
      icon: '\u{1F517}',
      category: 'developer',
      description: 'Unified REST APIs for retail price comparison, grocery data, streaming availability, and AI gateway access. Includes TypeScript and Python SDKs.',
      price: 0,
      monthlyPrice: 'Free tier',
      features: ['Retail API', 'Grocery API', 'Streaming API', 'AI Gateway'],
      status: 'available',
      users: '1,100+',
      rating: 4.8,
      url: 'https://api.quantrolix.ai',
    },
    {
      id: 'bourbon-hunter',
      name: 'Bourbon Hunter',
      icon: '\u{1F943}',
      category: 'lifestyle',
      description: 'Track bourbon prices, get alerts on rare finds, and manage your collection.',
      price: 9,
      monthlyPrice: '$9/mo',
      features: ['Price Tracking', 'Rare Alerts', 'Collection Manager', 'Tasting Notes'],
      status: 'available',
      users: '2,100+',
      rating: 4.8,
      url: 'https://apps.quantrolix.ai/bourbon-hunter',
    },
  ];

  const categories = [
    { id: 'all', name: 'All Apps', icon: '\u{1F310}' },
    { id: 'restaurant', name: 'Restaurant', icon: '\u{1F37D}\u{FE0F}' },
    { id: 'lifestyle', name: 'Lifestyle', icon: '\u{1F33F}' },
    { id: 'streaming', name: 'Streaming', icon: '\u{1F4FA}' },
    { id: 'smart-home', name: 'Smart Home', icon: '\u{1F3E0}' },
    { id: 'finance', name: 'Finance', icon: '\u{1F4B0}' },
    { id: 'design', name: 'Design', icon: '\u{1F3A8}' },
    { id: 'developer', name: 'Developer', icon: '\u{1F4BB}' },
    { id: 'mapping', name: 'Mapping', icon: '\u{1F5FA}\u{FE0F}' },
    { id: 'media', name: 'Media', icon: '\u{1F3AC}' },
  ];

  const filteredApps = quantrolixApps.filter((app) => {
    const matchesSearch = app.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         app.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || app.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const getStatusBadge = (status) => {
    const styles = {
      available: { bg: 'rgba(34, 197, 94, 0.2)', color: '#22c55e', text: 'Available' },
      'coming-soon': { bg: 'rgba(251, 191, 36, 0.2)', color: '#fbbf24', text: 'Coming Soon' },
      beta: { bg: 'rgba(147, 51, 234, 0.2)', color: '#9333ea', text: 'Beta' },
    };
    const s = styles[status] || styles.available;
    return (
      <span style={{
        padding: '4px 10px',
        background: s.bg,
        color: s.color,
        borderRadius: '12px',
        fontSize: '0.75rem',
        fontWeight: '600',
      }}>
        {s.text}
      </span>
    );
  };

  const handleLaunchApp = async (app) => {
    if (app.status === 'coming-soon') {
      toast.info(`${app.name} is coming soon! We'll notify you when it's ready.`);
      return;
    }

    setLaunchingApp(app.id);

    try {
      // Use SSO service for authenticated app launch
      const success = await ssoService.launchApp(app.id, true);

      if (success) {
        toast.success(`Launching ${app.name}...`);
        // Refresh recent apps list
        setRecentApps(ssoService.getRecentApps());
      } else {
        toast.info(`Opening ${app.name}...`);
      }
    } catch (error) {
      console.error('App launch error:', error);
      // Fallback to direct URL
      window.open(app.url, '_blank');
      toast.info(`Opening ${app.name}...`);
    } finally {
      setLaunchingApp(null);
    }
  };

  return (
    <div className="app-marketplace" style={{ padding: '30px' }}>
      {/* Header */}
      <div style={{ marginBottom: '30px' }}>
        <h1 style={{
          fontSize: '2rem',
          fontWeight: 'bold',
          marginBottom: '8px',
          background: 'linear-gradient(135deg, #667eea, #00d4ff)',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
        }}>
          App Marketplace
        </h1>
        <p style={{ color: 'rgba(255,255,255,0.6)' }}>
          Access all Quantrolix apps with your subscription
        </p>
      </div>

      {/* Search and Filters */}
      <div style={{
        display: 'flex',
        gap: '20px',
        marginBottom: '30px',
        flexWrap: 'wrap',
      }}>
        <div style={{ flex: '1', minWidth: '250px' }}>
          <input
            type="text"
            placeholder="Search apps..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{
              width: '100%',
              padding: '12px 16px',
              background: 'rgba(255,255,255,0.05)',
              border: '1px solid rgba(102, 126, 234, 0.3)',
              borderRadius: '10px',
              color: 'white',
              fontSize: '1rem',
            }}
          />
        </div>
        <div style={{
          display: 'flex',
          gap: '8px',
          flexWrap: 'wrap',
        }}>
          {categories.map((cat) => (
            <button
              key={cat.id}
              onClick={() => setSelectedCategory(cat.id)}
              style={{
                padding: '10px 16px',
                background: selectedCategory === cat.id
                  ? 'linear-gradient(135deg, #667eea, #00d4ff)'
                  : 'rgba(255,255,255,0.05)',
                border: selectedCategory === cat.id
                  ? 'none'
                  : '1px solid rgba(102, 126, 234, 0.3)',
                borderRadius: '8px',
                color: 'white',
                cursor: 'pointer',
                fontSize: '0.9rem',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
              }}
            >
              <span>{cat.icon}</span>
              <span>{cat.name}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Recently Used Apps */}
      {recentApps.length > 0 && (
        <div style={{ marginBottom: '30px' }}>
          <h3 style={{
            fontSize: '1rem',
            fontWeight: '600',
            marginBottom: '16px',
            color: 'rgba(255,255,255,0.8)',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
          }}>
            <span>{'\u{23F1}\u{FE0F}'}</span> Recently Used
          </h3>
          <div style={{
            display: 'flex',
            gap: '12px',
            flexWrap: 'wrap',
          }}>
            {recentApps.map((appId) => {
              const app = quantrolixApps.find(a => a.id === appId);
              if (!app) return null;
              return (
                <button
                  key={appId}
                  onClick={() => handleLaunchApp(app)}
                  disabled={launchingApp === appId}
                  style={{
                    padding: '12px 20px',
                    background: 'rgba(102, 126, 234, 0.15)',
                    border: '1px solid rgba(102, 126, 234, 0.3)',
                    borderRadius: '12px',
                    color: 'white',
                    cursor: launchingApp === appId ? 'wait' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    transition: 'all 0.2s',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = 'rgba(102, 126, 234, 0.25)';
                    e.currentTarget.style.borderColor = 'rgba(102, 126, 234, 0.5)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'rgba(102, 126, 234, 0.15)';
                    e.currentTarget.style.borderColor = 'rgba(102, 126, 234, 0.3)';
                  }}
                >
                  <span style={{ fontSize: '1.25rem' }}>{app.icon}</span>
                  <span style={{ fontWeight: '500' }}>{app.name}</span>
                  {launchingApp === appId && (
                    <span className="loading-spinner" style={{ width: '12px', height: '12px' }}></span>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Apps Grid */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
        gap: '24px',
      }}>
        {filteredApps.map((app, i) => (
          <motion.div
            key={app.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.05 }}
            style={{
              background: 'rgba(255,255,255,0.03)',
              border: '1px solid rgba(102, 126, 234, 0.2)',
              borderRadius: '16px',
              overflow: 'hidden',
              transition: 'all 0.3s',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = 'rgba(102, 126, 234, 0.5)';
              e.currentTarget.style.transform = 'translateY(-4px)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = 'rgba(102, 126, 234, 0.2)';
              e.currentTarget.style.transform = 'translateY(0)';
            }}
          >
            {/* App Header */}
            <div style={{
              padding: '24px',
              borderBottom: '1px solid rgba(102, 126, 234, 0.1)',
            }}>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                marginBottom: '12px',
              }}>
                <div style={{
                  fontSize: '2.5rem',
                  width: '60px',
                  height: '60px',
                  background: 'rgba(102, 126, 234, 0.1)',
                  borderRadius: '14px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}>
                  {app.icon}
                </div>
                {getStatusBadge(app.status)}
              </div>
              <h3 style={{
                fontSize: '1.25rem',
                fontWeight: '600',
                marginBottom: '8px',
              }}>
                {app.name}
              </h3>
              <p style={{
                color: 'rgba(255,255,255,0.6)',
                fontSize: '0.9rem',
                lineHeight: '1.5',
                minHeight: '45px',
              }}>
                {app.description}
              </p>
            </div>

            {/* Features */}
            <div style={{
              padding: '16px 24px',
              borderBottom: '1px solid rgba(102, 126, 234, 0.1)',
            }}>
              <div style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: '8px',
              }}>
                {app.features.map((feature, fi) => (
                  <span
                    key={fi}
                    style={{
                      padding: '4px 10px',
                      background: 'rgba(102, 126, 234, 0.1)',
                      borderRadius: '6px',
                      fontSize: '0.8rem',
                      color: 'rgba(255,255,255,0.8)',
                    }}
                  >
                    {feature}
                  </span>
                ))}
              </div>
            </div>

            {/* Stats and Action */}
            <div style={{
              padding: '16px 24px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}>
              <div>
                <div style={{
                  fontSize: '1.25rem',
                  fontWeight: 'bold',
                  color: '#00d4ff',
                }}>
                  {app.monthlyPrice}
                </div>
                <div style={{
                  fontSize: '0.8rem',
                  color: 'rgba(255,255,255,0.5)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                }}>
                  <span>{'\u{2B50}'} {app.rating}</span>
                  <span>{'\u{2022}'}</span>
                  <span>{app.users} users</span>
                </div>
              </div>
              <button
                onClick={() => handleLaunchApp(app)}
                disabled={app.status === 'coming-soon' || launchingApp === app.id}
                style={{
                  padding: '10px 20px',
                  background: app.status === 'coming-soon'
                    ? 'rgba(255,255,255,0.1)'
                    : launchingApp === app.id
                      ? 'rgba(102, 126, 234, 0.5)'
                      : 'linear-gradient(135deg, #667eea, #00d4ff)',
                  border: 'none',
                  borderRadius: '8px',
                  color: 'white',
                  cursor: app.status === 'coming-soon' || launchingApp === app.id ? 'not-allowed' : 'pointer',
                  fontWeight: '600',
                  opacity: app.status === 'coming-soon' ? 0.5 : 1,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                }}
              >
                {launchingApp === app.id ? (
                  <>
                    <span className="loading-spinner" style={{ width: '14px', height: '14px' }}></span>
                    Launching...
                  </>
                ) : app.status === 'coming-soon' ? 'Notify Me' : 'Launch App'}
              </button>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Empty State */}
      {filteredApps.length === 0 && (
        <div style={{
          textAlign: 'center',
          padding: '60px 20px',
          color: 'rgba(255,255,255,0.5)',
        }}>
          <div style={{ fontSize: '3rem', marginBottom: '16px' }}>{'\u{1F50D}'}</div>
          <h3 style={{ marginBottom: '8px' }}>No apps found</h3>
          <p>Try adjusting your search or filters</p>
        </div>
      )}

      {/* Stats Banner */}
      <div style={{
        marginTop: '40px',
        padding: '30px',
        background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.1), rgba(147, 51, 234, 0.1))',
        border: '1px solid rgba(102, 126, 234, 0.2)',
        borderRadius: '16px',
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
        gap: '20px',
        textAlign: 'center',
      }}>
        <div>
          <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#00d4ff' }}>13</div>
          <div style={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.9rem' }}>Apps Available</div>
        </div>
        <div>
          <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#00d4ff' }}>11,000+</div>
          <div style={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.9rem' }}>Active Users</div>
        </div>
        <div>
          <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#00d4ff' }}>99.9%</div>
          <div style={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.9rem' }}>Uptime</div>
        </div>
        <div>
          <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#00d4ff' }}>4.7</div>
          <div style={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.9rem' }}>Avg Rating</div>
        </div>
      </div>
    </div>
  );
};

export default AppMarketplace;
